import { useMemo } from "react";
import { useAuth } from "./useAuth";
import {
  getEffectivePermissions,
  hasAccessPermission,
} from "../constants/access-control";

/**
 * Hook that exposes the effective permission set for the current user.
 *
 * Effective permissions are the union of:
 *   - permissions resolved by the API (`user.access.permissions`)
 *   - authority-default permissions used as a UX fallback when the user has
 *     no tenant roles yet (kept in sync with the API resolver).
 *
 * Components should prefer `hasPermission` / `hasAnyPermission` for UI gating
 * and rely on the API to re-validate before performing protected actions.
 */
export const usePermissions = () => {
  const { user } = useAuth();

  const permissions = useMemo(() => getEffectivePermissions(user), [user]);

  const roles = useMemo(() => user?.access?.roles || [], [user?.access?.roles]);

  const hasPermission = (permission) => hasAccessPermission(user, permission);

  const hasAnyPermission = (permissionList = []) =>
    permissionList.some((permission) => hasPermission(permission));

  const hasAllPermissions = (permissionList = []) =>
    permissionList.every((permission) => hasPermission(permission));

  const isSuperAdmin = user?.authority === "superAdmin";
  const isAccountHolder =
    user?.accountHolder === true || user?.authority === "owner";
  // True for any user whose legacy authority is admin or higher. Useful as a
  // backwards-compatible gate for surfaces that don't yet have (or have had
  // retired) granular permissions — for example the Inventory pages.
  const isTenantAdmin =
    isSuperAdmin ||
    isAccountHolder ||
    user?.authority === "admin";

  return {
    access: user?.access,
    hasAllPermissions,
    hasAnyPermission,
    hasPermission,
    isAccountHolder,
    isSuperAdmin,
    isTenantAdmin,
    permissions,
    roles,
  };
};

export default usePermissions;
